import {useRouter} from "vue-router";
import {computed, reactive, ref} from "vue";
import {isDef, isNull, itExist} from "@/utils/general-utils";
import {getLocalizedDate, LocalizedDate} from "@/utils/date-utils";
import ApiService, {ProductApi, SerialStatusEnum, tableConsumerText} from "@/api/apiService";
import {format} from "date-fns-tz";
import store from "@/store";
import {ConfirmTypes, CurrentConfirmation, TableProduct} from "@/composables/customerFilePage/model";

export function useTable(customerData: any, areActionsDisabled: any) {
  const router = useRouter();

  const productData = computed<TableProduct[]>(() => {
    if (!itExist(customerData.value?.products) || !itExist(customerData.value?.user.serials)) {
      return [];
    }

    return customerData.value.products.map((product: ProductApi): TableProduct => {
      const serial: any = customerData.value.user.serials.find((serialItem: any) => serialItem.id === product.reference);
      return {
        serialNumber: serial.serialNumber,
        reference: serial.id,
        status: serial.status,
        localizedCreationDate: !isNull(serial.localizedCreationDate) ? getLocalizedDate(serial.localizedCreationDate as LocalizedDate) : '',
        countryCode: serial.countryCode,
        locale: serial.locale,
        origin: tableConsumerText.get(serial.origin) ?? null,
        deletedAt: serial?.deletedAt ?? null,
        name: product?.name ?? "",
        photoUrl: product?.photoUrl ?? "",
        purchaseDate: isDef(product?.retailDate) && !isNull(product?.retailDate) ? format(new Date(product.retailDate as string), 'PPPp') : '', // Date come from SN360 = Unknown timezone,
      };
    });
  });

  const currentConfirm = reactive<CurrentConfirmation>({
    serialId: undefined,
    type: undefined,
  });

  function toggleConfirmation(serialId?: string, type?: ConfirmTypes, isDisabled = false): void {
    if (isDisabled) {
      return;
    }

    currentConfirm.serialId = serialId;
    currentConfirm.type = type;
  }

  async function sendLink(serialId: string): Promise<void> {
    areActionsDisabled.value = true;
    try {
      const response = await ApiService.sendSerialLink(serialId);
      store.dispatch('customerPage/showNotification', "The access link has been sent.");
    } catch (error) {
      console.error('error', error);
      store.dispatch(
        'customerPage/showNotification',
        ApiService.getPublicResponseAsText(error.response.data),
      );
    }
    areActionsDisabled.value = false;
    toggleConfirmation();
    store.dispatch('customerPage/fetchUser');

  }

  async function dissociateProduct(serialId: string, userId: string): Promise<void> {
    areActionsDisabled.value = true;
    try {
      const response = await ApiService.dissociateSerial(serialId, userId);
      store.dispatch('customerPage/showNotification', "The serial has been removed.");
      store.dispatch('customerPage/fetchUser');
    } catch (error) {
      console.error(error);
      store.dispatch(
        'customerPage/showNotification',
        ApiService.getPublicResponseAsText(error.response.data),
      );
    }
    areActionsDisabled.value = false;
    toggleConfirmation();

  }

  async function deleteStatus(serialId: string): Promise<void> {
    areActionsDisabled.value = true;
    try {
      const response = await ApiService.removeTagSerial(serialId);
      store.dispatch('customerPage/showNotification', "The serial status has been removed.");
      store.dispatch('customerPage/fetchUser');
    } catch (error) {
      console.error(error);
      store.dispatch(
        'customerPage/showNotification',
        ApiService.getPublicResponseAsText(error.response.data),
      );
    }
    areActionsDisabled.value = false;
    toggleConfirmation();
  }

  function redirectToProduct(serialId: string, customerId: string, langCode: string, locale: string): void {
    // router.push({name: 'product-file-page', params: {serialNumber: serialNumber, customerId: customerId}})
    const routeData = router.resolve({
      name: 'product-file-page',
      params: {serialId: serialId, customerId: customerId},
      query: {locale: locale ?? langCode ?? 'en_GB'},
    });
    window.open(routeData.href, '_blank');
  }

  function isProductOrange(product: TableProduct): boolean {
    return [SerialStatusEnum.NOT_SOLD].includes(product.status);
  }

  return {
    productData,
    ConfirmTypes,
    redirectToProduct,
    toggleConfirmation,
    sendLink,
    currentConfirm,
    dissociateProduct,
    deleteStatus,
    isProductOrange,
  };
}

export function useForm() {

  const consumerInfoRadioRef = ref('email');
  const consumerEmailRef = ref('');
  const consumerPhoneRef = ref('');
  const consumerFirstNameRef = ref('');
  const consumerLastNameRef = ref('');

  return {
    consumerInfoRadioRef,
    consumerEmailRef,
    consumerPhoneRef,
    consumerFirstNameRef,
    consumerLastNameRef,
  };
}

export function useHistory(customerData: any) {
  const historyData = computed(() => {
    if (!itExist(customerData.value?.histories)) {
      return [];
    }

    return customerData.value.histories;
  });

  return {
    historyData,
  };
}
